import React from "react";
import { shouldShowDataTablePagination } from "./use-data-table-pagination";

const integerFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

const formatInteger = (value) => integerFormatter.format(Number(value) || 0);

const NavigationButton = ({
  children,
  disabled,
  icon,
  label,
  onClick,
  compact = false,
}) => (
  <button
    type="button"
    className={`dhn-data-table__page-button${compact ? " is-compact" : ""}`}
    disabled={disabled}
    onClick={onClick}
    aria-label={label}
    title={label}
  >
    {icon && <i className={`fa ${icon}`} aria-hidden="true" />}
    {children}
  </button>
);

export default function DataTablePagination({
  canNext,
  canPrevious,
  className,
  data,
  onPageChange,
  onPageSizeChange,
  page,
  pages,
  pageSize,
  pageSizeOptions,
  showPageSizeOptions,
  sortedData,
  style,
  totalRows,
}) {
  if (!shouldShowDataTablePagination({ canNext, canPrevious })) return null;

  const totalPages = Math.max(Number(pages) || 0, 1);
  const currentPage = Math.min(Math.max(Number(page) || 0, 0), totalPages - 1);
  const clientTotal = Array.isArray(sortedData)
    ? sortedData.length
    : Array.isArray(data)
    ? data.length
    : 0;
  const total = Number.isFinite(totalRows) ? totalRows : clientTotal;
  const firstResult = total ? currentPage * pageSize + 1 : 0;
  const lastResult = total ? Math.min((currentPage + 1) * pageSize, total) : 0;
  const resultLabel = total === 1 ? "resultado" : "resultados";

  return (
    <div
      className={`dhn-data-table__pagination${className ? ` ${className}` : ""}`}
      style={style}
      role="navigation"
      aria-label="Paginación de la tabla"
    >
      <div className="dhn-data-table__pagination-summary">
        <span>
          Mostrando <strong>{formatInteger(firstResult)}</strong> a{" "}
          <strong>{formatInteger(lastResult)}</strong> de{" "}
          <strong>{formatInteger(total)}</strong> {resultLabel}
        </span>

        {showPageSizeOptions && (
          <label className="dhn-data-table__page-size">
            <span>Filas por página</span>
            <select
              value={pageSize}
              onChange={({ target }) => onPageSizeChange(Number(target.value))}
              aria-label="Filas por página"
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="dhn-data-table__pagination-navigation">
        <NavigationButton
          compact
          disabled={!canPrevious}
          icon="fa-angle-double-left"
          label="Primera página"
          onClick={() => onPageChange(0)}
        />
        <NavigationButton
          disabled={!canPrevious}
          icon="fa-angle-left"
          label="Página anterior"
          onClick={() => onPageChange(currentPage - 1)}
        >
          <span className="dhn-data-table__page-button-label">Anterior</span>
        </NavigationButton>

        <span className="dhn-data-table__page-indicator" aria-live="polite">
          Página <strong>{formatInteger(currentPage + 1)}</strong> de{" "}
          <strong>{formatInteger(totalPages)}</strong>
        </span>

        <NavigationButton
          disabled={!canNext}
          label="Página siguiente"
          onClick={() => onPageChange(currentPage + 1)}
        >
          <span className="dhn-data-table__page-button-label">Siguiente</span>
          <i className="fa fa-angle-right" aria-hidden="true" />
        </NavigationButton>
        <NavigationButton
          compact
          disabled={!canNext}
          icon="fa-angle-double-right"
          label="Última página"
          onClick={() => onPageChange(totalPages - 1)}
        />
      </div>
    </div>
  );
}
